import React, { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import { isEmpty } from 'lodash'
import {
  Box,
  Chip,
  Paper,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@material-ui/core'
import {
  ArrowRight as ArrowRightIcon,
  Check as CheckIcon,
  Target as TargetIcon,
  X as XIcon,
} from 'react-feather'

import {
  ApplicabilityDeclaration,
  ClaimsAnalysis,
  DisputeArguments,
  DisputeDocument,
} from './components'

import constants from 'constants/index'
import helpers from 'helpers'
import useStyles from './styles'

const DisputeResult = ({ result, alternatives }) => {
  const { TAB_VALUES } = constants.dai.disputes

  const classes = useStyles()
  const [activeTab, setActiveTab] = useState(TAB_VALUES.CLAIMS)

  const {
    question,
    report,
    decisions,
    dispute,
    claims,
    specialist_findings: specialistFindings,
    derived_findings: derivedFindings,
  } = result || {}

  const auditorLetter = report?.auditor_alternative_letter
  const claimedLetter = report?.unit_alternative_letter
  const applicability = result?.applicability || dispute?.applicability
  const meritFloor = result?.merit_floor
  const recommendedLetter = useMemo(
    () =>
      helpers.dai.recommendedAlternative({
        decisions,
        applicability,
        meritFloor,
        claimedLetter,
        auditorLetter,
      }),
    [decisions, applicability, meritFloor, claimedLetter, auditorLetter],
  )

  if (!result) return null

  const auditorName = helpers.dai.getAlternativeName(
    auditorLetter,
    alternatives,
  )
  const claimedName = helpers.dai.getAlternativeName(
    claimedLetter,
    alternatives,
  )
  const recommendedName = helpers.dai.getAlternativeName(
    recommendedLetter,
    alternatives,
  )

  const levelDiff = dispute?.analysis?.level_difference
  const strategy = dispute?.analysis?.overall_strategy
  const conclusion = dispute?.conclusion
  const documentMd = dispute?.document_md
  const documentMdAppellative = dispute?.document_md_appellative
  const argumentsList = dispute?.arguments
  const evidenceMap = dispute?.evidence_map
  const hasApplicability = !!applicability && !isEmpty(applicability.items)

  const decisionEntries = Object.values(decisions || {})
  const admissibleCount = decisionEntries.filter((d) => d.is_admissible).length
  const groundlessCount = decisionEntries.filter((d) => !d.is_admissible).length

  const showFallback =
    recommendedLetter &&
    claimedLetter &&
    recommendedLetter.toUpperCase() !== claimedLetter.toUpperCase()

  return (
    <Box className={classes.root}>
      <Paper variant="outlined" className={classes.summaryCard}>
        <Box className={classes.alternativesRow}>
          <Box className={classes.alternativeBadge}>
            <Typography className={classes.badgeLabel}>Auditor</Typography>
            <Typography
              className={clsx(classes.badgeLetter, classes.badgeLetterAuditor)}
            >
              {auditorLetter || '—'}
            </Typography>

            {auditorName && (
              <Typography className={classes.badgeName}>
                {auditorName}
              </Typography>
            )}
          </Box>

          <ArrowRightIcon size={20} className={classes.arrowIcon} />

          <Box className={classes.alternativeBadge}>
            <Typography className={classes.badgeLabel}>
              Pleito original
            </Typography>
            <Typography
              className={clsx(classes.badgeLetter, classes.badgeLetterUnit)}
            >
              {claimedLetter || '—'}
            </Typography>

            {claimedName && (
              <Typography className={classes.badgeName}>
                {claimedName}
              </Typography>
            )}
          </Box>

          <ArrowRightIcon size={20} className={classes.arrowIcon} />

          <Tooltip
            title={
              showFallback
                ? `A DAI sustenta apenas até ${recommendedLetter} - o pleito original ${claimedLetter} é parcialmente inviável.`
                : 'A DAI sustenta o pleito original.'
            }
          >
            <Box
              className={clsx(
                classes.alternativeBadge,
                classes.alternativeBadgeRecommended,
              )}
            >
              <Typography className={classes.badgeLabel}>
                Recomendado pela DAI
              </Typography>
              <Typography
                className={clsx(
                  classes.badgeLetter,
                  classes.badgeLetterRecommended,
                )}
              >
                {recommendedLetter || '—'}
              </Typography>

              {recommendedName && (
                <Typography className={classes.badgeName}>
                  {recommendedName}
                </Typography>
              )}
            </Box>
          </Tooltip>
        </Box>

        {levelDiff > 0 && (
          <Typography className={classes.levelText}>
            {levelDiff} nota(s) de diferença entre auditor e pleito original
          </Typography>
        )}

        {question && (
          <Box>
            <Typography className={classes.questionLabel}>Questão</Typography>
            <Typography className={classes.questionText}>{question}</Typography>
          </Box>
        )}

        {(admissibleCount > 0 || groundlessCount > 0) && (
          <Box className={classes.countersRow}>
            {admissibleCount > 0 && (
              <Tooltip title="Pontos com fundamento: a DAI identificou argumentos válidos para contestar.">
                <Chip
                  size="small"
                  icon={<CheckIcon size={12} />}
                  label={`${admissibleCount} com fundamento`}
                  className={clsx(
                    classes.counterChip,
                    classes.counterAdmissible,
                  )}
                />
              </Tooltip>
            )}
            {groundlessCount > 0 && (
              <Tooltip title="Pontos sem fundamento: a DAI não identificou base suficiente para contestar.">
                <Chip
                  size="small"
                  icon={<XIcon size={12} />}
                  label={`${groundlessCount} sem fundamento`}
                  className={clsx(
                    classes.counterChip,
                    classes.counterGroundless,
                  )}
                />
              </Tooltip>
            )}
          </Box>
        )}

        {strategy && (
          <Box className={classes.strategyBox}>
            <TargetIcon size={16} className={classes.strategyIcon} />
            <Box className={classes.strategyContent}>
              <Typography className={classes.strategyLabel}>
                Estratégia
              </Typography>
              <Typography className={classes.strategyText}>
                {strategy}
              </Typography>
            </Box>
          </Box>
        )}

        {conclusion && (
          <Box className={classes.conclusionBox}>
            <Typography className={classes.conclusionLabel}>
              Conclusão e pleito
            </Typography>
            <Typography className={classes.conclusionText}>
              {conclusion}
            </Typography>
          </Box>
        )}
      </Paper>

      <Tabs
        value={activeTab}
        onChange={(_event, value) => setActiveTab(value)}
        indicatorColor="primary"
        textColor="primary"
        className={classes.tabs}
        variant="scrollable"
        scrollButtons="auto"
      >
        <Tab
          value={TAB_VALUES.CLAIMS}
          label={`Pontos (${claims?.length || 0})`}
        />
        <Tab
          value={TAB_VALUES.ARGUMENTS}
          label={`Argumentos (${argumentsList?.length || 0})`}
        />
        <Tab
          value={TAB_VALUES.APPLICABILITY}
          label="Aderência aos requisitos"
        />
        <Tab value={TAB_VALUES.DOCUMENT} label="Contestação" />
      </Tabs>

      {activeTab === TAB_VALUES.CLAIMS && (
        <Box className={classes.tabPanel}>
          <ClaimsAnalysis
            claims={claims}
            decisions={decisions}
            specialistFindings={specialistFindings}
            derivedFindings={derivedFindings}
          />
        </Box>
      )}

      {activeTab === TAB_VALUES.ARGUMENTS && (
        <Box className={classes.tabPanel}>
          <DisputeArguments arguments={argumentsList} />
        </Box>
      )}

      {activeTab === TAB_VALUES.APPLICABILITY && (
        <Box className={classes.tabPanel}>
          {hasApplicability ? (
            <ApplicabilityDeclaration
              applicability={applicability}
              evidenceMap={evidenceMap}
            />
          ) : (
            <Typography variant="body2" color="textSecondary">
              Aderência aos requisitos não disponível para esta análise.
            </Typography>
          )}
        </Box>
      )}

      {activeTab === TAB_VALUES.DOCUMENT && (
        <Box className={clsx(classes.tabPanel, classes.documentSection)}>
          <DisputeDocument
            documentMd={documentMd}
            documentMdAppellative={documentMdAppellative}
          />
        </Box>
      )}
    </Box>
  )
}

DisputeResult.propTypes = {
  result: PropTypes.object,
  alternatives: PropTypes.array,
}

DisputeResult.defaultProps = {
  alternatives: [],
}

export default DisputeResult
